import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getProjectById } from '../../../../../services/projectService';
import { isAdmin, isClient } from '../../../../../utils/share';
import { PROJECT_EDITOR_ROLE } from '../../../../../Constants/ProjectConstants';

// Creating a context
const ProjectContext = createContext({
  projectDetails: {},
  isLoading: false,
  isEditPermission: false,
  projectRole: '',
  refetchProject: () => {},
  setProjectDetails: () => {},
});

export const ProjectProvider = ({ children }) => {
  const { id } = useParams();
  const accountData = useSelector((state) => state?.login?.user || null);
  // get path name
  const { pathname } = useLocation();
  const [projectDetails, setProjectDetails] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [refetcher, setRefetcher] = useState(false);

  const refetchProject = useCallback(() => setRefetcher((prev) => !prev), []);

  const projectRole =
    projectDetails?.projectmember?.find((data) => data.userId === accountData?.id)?.projectRoles?.roleName ?? '';

  const userIsAdmin = isAdmin();
  const userIsClient = isClient();

  const isEditPermission = isAdmin() || PROJECT_EDITOR_ROLE.includes(projectRole);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      setLoading(true);
      try {
        const Projects = await getProjectById(id);
        setProjectDetails(Projects?.data?.data);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [id, refetcher, pathname]);

  return (
    <ProjectContext.Provider
      value={{ projectDetails, refetchProject, isLoading, setProjectDetails, isEditPermission, projectRole , userIsAdmin,userIsClient }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

ProjectProvider.propTypes = {
  children: PropTypes.node,
};
